/**
 * serverTime.ts — turn API timestamp strings into a correct local rendering.
 *
 * WHY THIS EXISTS
 * ---------------
 * Almost every timestamp in this system is written on the server as `DateTime.UtcNow`, but many
 * of them are read back out of SQL Server `datetime2` columns through EF Core. `datetime2` stores
 * no offset, so the CLR value comes back with `DateTimeKind.Unspecified`, and System.Text.Json
 * therefore serialises it WITHOUT a trailing `Z`:
 *
 *     "2026-08-06T09:12:33.12"      <-- a UTC instant, but nothing in the string says so
 *
 * ECMAScript is very specific about that string. A date-TIME form carrying no timezone
 * designator is interpreted as *browser-local* time (a date-ONLY form such as "2026-08-06",
 * by contrast, is interpreted as UTC). So in Karachi `new Date("2026-08-06T09:12:33.12")`
 * yields 09:12 PKT — five hours earlier than the instant that was actually recorded. Printed
 * with `toLocaleString()` that reads as a plausible-but-wrong time, so the symptom looks like a
 * permanently slow clock rather than an obvious bug.
 *
 * `parseServerDate` fixes it by appending `Z` to exactly those strings that are a date-time form
 * with no designator, and leaving everything else alone. Values that already serialise correctly
 * (anything the server produced from a `Kind=Utc` value — those do get a `Z` — and anything with
 * a `+05:00`-style offset) pass through untouched. That is the point of the helper: it is always
 * safe to route *every* server timestamp through it, because a value that already carries a
 * designator is never shifted a second time.
 */

/** A trailing timezone designator: `Z`/`z`, or a `+hh:mm` / `-hhmm` offset. */
const TZ_DESIGNATOR = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * A date-TIME form with NO designator — the shape that ECMAScript reads as browser-local and
 * that .NET emits for a `Kind=Unspecified` value. Anchored at both ends, so a string that
 * already ends in `Z`/offset cannot match, and neither can a date-only string.
 */
const NAKED_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

const DEFAULT_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

export interface FormatServerDateOptions extends Intl.DateTimeFormatOptions {
  /** Rendered when the value is missing or unparseable. Defaults to an em dash. */
  fallback?: string;
}

/**
 * Parse a timestamp string produced by the API into a `Date`.
 *
 * Rule, in order:
 *   1. null / undefined / blank  -> `null`
 *   2. a space separator (`"2026-08-06 09:12:33"`) is normalised to `T`
 *   3. a date-TIME form with NO `Z` and NO `±hh:mm` offset is treated as **UTC** (`Z` appended)
 *   4. anything else is parsed exactly as given (already-tagged values are never shifted)
 *   5. an invalid result yields `null` — never an `Invalid Date`
 */
export function parseServerDate(value?: string | null): Date | null {
  if (value === null || value === undefined) return null;

  let s = String(value).trim();
  if (!s) return null;

  // "2026-08-06 09:12:33[...]" -> "2026-08-06T09:12:33[...]" (SQL-style separator, and the
  // lowercase `t` some serialisers emit). Only the separator position is touched.
  s = s.replace(/^(\d{4}-\d{2}-\d{2})[ t]/, '$1T');

  // The whole reason this file exists — see the header comment.
  if (NAKED_DATE_TIME.test(s) && !TZ_DESIGNATOR.test(s)) s = `${s}Z`;

  const d = new Date(s);
  // `new Date('nonsense')` does NOT throw; it returns an Invalid Date whose getTime() is NaN,
  // and whose toLocaleString() is the literal string "Invalid Date". A try/catch cannot see
  // that, so the NaN check is the only reliable guard.
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a server timestamp in the VIEWER's own locale and timezone.
 * Locale is `undefined` on purpose (browser default) rather than a hardcoded one, and the zone
 * is whatever `Intl.DateTimeFormat` resolves for this machine.
 */
export function formatServerDateTime(
  value?: string | null,
  opts?: FormatServerDateOptions,
): string {
  const { fallback = '—', ...formatOptions } = opts ?? {};
  const d = parseServerDate(value);
  if (!d) return fallback;

  const options = Object.keys(formatOptions).length > 0 ? formatOptions : DEFAULT_FORMAT;
  try {
    return new Intl.DateTimeFormat(undefined, options).format(d);
  } catch {
    return fallback;
  }
}

/**
 * The same instant rendered as unambiguous UTC — for tooltips/exports so support can
 * cross-check against the raw database value. Returns `''` when unparseable.
 */
export function formatServerDateTimeUtc(value?: string | null): string {
  const d = parseServerDate(value);
  if (!d) return '';
  try {
    const s = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(d);
    return `${s} UTC`;
  } catch {
    return `${d.toISOString()} UTC`;
  }
}

/** Canonical ISO-8601 UTC string (for spreadsheet columns). `''` when unparseable. */
export function toUtcIsoString(value?: string | null): string {
  const d = parseServerDate(value);
  return d ? d.toISOString() : '';
}

/** Relative time: "just now", "5m ago", "3h ago", "2d ago". `''` when unparseable. */
export function timeAgoFrom(value?: string | null): string {
  const d = parseServerDate(value);
  if (!d) return '';

  const diff = Date.now() - d.getTime();
  // Small negatives are ordinary clock skew between the server and this browser.
  if (diff < 60000) return 'just now';

  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 365) return `${days}d ago`;

  return `${Math.floor(days / 365)}y ago`;
}

/** The viewer's IANA timezone, e.g. "Asia/Karachi". Falls back to "UTC". */
export function getViewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const serverTime = {
  parseServerDate,
  formatServerDateTime,
  formatServerDateTimeUtc,
  toUtcIsoString,
  timeAgoFrom,
  getViewerTimeZone,
};

export default serverTime;
